import { computed, effect, inject, Injectable, Injector, NgZone, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { filter, firstValueFrom } from 'rxjs';
import { API_DATETIME_FORMAT } from '../../shared/constants/date-format';
import {
  ModalUpdateUserEmailComponent,
  ModalUpdateUserEmailData,
  ModalUpdateUserEmailResult,
} from '../../shared/modals/modal-update-user-email/modal-update-user-email.component';
import { apiQuery } from '../api/api-query';
import { AuthActions, AuthSelectors } from '../state/auth-state';
import { HttpStudentService } from './http/http-student.service';
import { IsApplicationService } from './is-application.service';
import { ModalService } from './modal.service';
import { RouteDataService } from './route-data.service';

const DAILY_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;
// un simple setInterval de 24 h serait retardé ou jamais déclenché (onglet en arrière-plan, veille,
// app suspendue) : on vérifie souvent si le contrôle est dû, et à chaque retour sur l'app
const DAILY_CHECK_TICK_MS = 60 * 60 * 1000;

/**
 * Quand nos emails n'arrivent plus à l'adresse de l'utilisateur (`emailPermanentFailureAt`), ouvre
 * la modale de changement d'email, non fermable tant qu'il ne l'a pas mise à jour.
 *
 * Détection au login et au recoverSession (le profil qu'ils chargent porte déjà le champ), puis
 * une fois par jour pour un onglet ou une app jamais rechargés.
 *
 * Comme la modale de mise à jour de l'app, elle ne s'ouvre qu'à une fin de navigation, pour ne pas
 * couper l'utilisateur au milieu d'une action. Une route s'y soustrait avec la data
 * `hideEmailPermanentFailureModal` (la confirmation de la nouvelle adresse, qui la rend caduque).
 */
@Injectable({
  providedIn: 'root',
})
export class EmailPermanentFailureService {
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private store = inject(Store);
  private httpStudentService = inject(HttpStudentService);
  private isApplicationService = inject(IsApplicationService);
  private modalService = inject(ModalService);
  private routeDataService = inject(RouteDataService);

  private rawStudent = this.store.selectSignal(AuthSelectors.rawStudent);
  private isLogged = this.store.selectSignal(AuthSelectors.isLogged);
  private impersonatedUser = this.store.selectSignal(AuthSelectors.impersonateUser);

  // le store est la seule source : rempli au login et au recoverSession, mis à jour par le contrôle
  // quotidien, remis à null par la modale quand l'utilisateur demande à changer d'adresse
  private hasEmailPermanentFailure = computed(() => !!this.rawStudent()?.emailPermanentFailureAt);

  // un admin qui se fait passer pour un étudiant n'a pas à changer l'email de ce dernier
  private mustUpdateEmail = computed(
    () => this.isLogged() && this.hasEmailPermanentFailure() && !this.impersonatedUser()
  );

  private navigationEnd = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    { initialValue: null }
  );

  private studentProfileQuery = apiQuery(() => this.httpStudentService.getMyStudentProfile(), {
    onError: (apiError) => console.error('contrôle quotidien de l’email en échec', apiError),
  });

  private lastCheckedAt = 0;
  private dialogRef: MatDialogRef<
    ModalUpdateUserEmailComponent,
    ModalUpdateUserEmailResult
  > | null = null;

  init() {
    // un nouveau profil chargé (login, recoverSession) vaut contrôle
    effect(
      () => {
        if (this.isLogged()) untracked(() => (this.lastCheckedAt = Date.now()));
      },
      { injector: this.injector }
    );

    // ouverture : seulement à une fin de navigation. Au recoverSession, la navigation initiale
    // arrive après le chargement du profil (l'initializer l'attend) ; au login, c'est la
    // redirection qui suit le chargement ; après le contrôle quotidien, la prochaine navigation
    effect(
      () => {
        if (!this.navigationEnd()) return;

        untracked(() => {
          // lu après coup : `lastRouteData` est déjà à jour quand l'effet s'exécute
          const isHiddenOnRoute =
            !!this.routeDataService.lastRouteData()?.hideEmailPermanentFailureModal;
          if (this.mustUpdateEmail() && !isHiddenOnRoute) this.openModal();
        });
      },
      { injector: this.injector }
    );

    // fermeture : tout de suite, sans attendre de navigation (demande de changement réussie,
    // adresse confirmée, déconnexion y compris forcée, adresse corrigée sur un autre appareil)
    effect(
      () => {
        if (!this.mustUpdateEmail()) untracked(() => this.dialogRef?.close(false));
      },
      { injector: this.injector }
    );

    setInterval(() => this.checkIfDue(), DAILY_CHECK_TICK_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.checkIfDue();
    });

    if (this.isApplicationService.isApplication()) {
      App.addListener('resume', () => {
        this.ngZone.run(() => this.checkIfDue());
      });
    }
  }

  private async checkIfDue() {
    if (!this.isLogged()) return;
    if (Date.now() - this.lastCheckedAt < DAILY_CHECK_INTERVAL_MS) return;

    // posé avant l'appel : le tick, le retour sur l'onglet et le resume peuvent tomber ensemble
    this.lastCheckedAt = Date.now();

    const studentProfile = await this.studentProfileQuery.load();
    if (studentProfile === null || !this.isLogged()) return;

    this.storeEmailPermanentFailureAt(studentProfile.emailPermanentFailureAt);
  }

  private storeEmailPermanentFailureAt(emailPermanentFailureAt: Date | null) {
    // `state.student` garde la forme de l'api : on y remet une chaîne, pas la `Date` du schéma
    const apiEmailPermanentFailureAt = emailPermanentFailureAt
      ? moment(emailPermanentFailureAt).format(API_DATETIME_FORMAT)
      : null;

    this.store.dispatch(
      AuthActions.updateMyEmailPermanentFailureAt({
        emailPermanentFailureAt: apiEmailPermanentFailureAt,
      })
    );
  }

  private async openModal() {
    if (this.dialogRef) return;

    const modalData: ModalUpdateUserEmailData = { isForced: true };
    this.dialogRef = this.modalService.openNewDesign(ModalUpdateUserEmailComponent, modalData, {
      disableClose: true,
      autoFocus: false,
    });

    // la modale remet elle-même `emailPermanentFailureAt` à null dans le store en cas de succès
    await firstValueFrom(this.dialogRef.afterClosed());
    this.dialogRef = null;
  }
}
